import React, { createContext, useContext, useState, useEffect } from "react";
import packageJson from "../../package.json";

import { errorLogService } from "../services/errorLogService";
import {
  detectSuspectedPwaUninstall,
  markPwaStandaloneLaunch,
} from "../services/pwaTelemetryService";

const PWAContext = createContext();

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error("usePWA must be used within a PWAProvider");
  }
  return context;
};

export const PWAProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstallPromptPending, setIsInstallPromptPending] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isWebview, setIsWebview] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const logSuspectedUninstall = ({ isInstallable: installable, platform, detectionMode }) => {
      const telemetryEvent = detectSuspectedPwaUninstall({
        version: packageJson.version,
        isStandalone: false,
        isInstallable: installable,
        isWebview: webview,
        platform,
        detectionMode,
      });

      if (telemetryEvent && isMounted) {
        errorLogService.logError(telemetryEvent);
      }
    };

    // Detect Standalone Mode
    const checkStandalone = () => {
      const isStandaloneMode = window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;
      setIsStandalone(isStandaloneMode);
      return isStandaloneMode;
    };

    // Detect OS and Browser/Webview
    const ua = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(ua);
    const android = /android/.test(ua);
    const webview = /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(ua) || // iOS Webview
      ua.includes("wv") || // Android Webview
      ua.includes("telegram"); // Telegram specifically

    setIsIOS(ios);
    setIsAndroid(android);
    setIsWebview(webview);
    const standaloneMode = checkStandalone();
    const platform = ios ? "ios" : android ? "android" : "web";

    if (standaloneMode) {
      markPwaStandaloneLaunch({ version: packageJson.version });
    }

    // Для iOS признаком "устанавливаемости" является то, что это iOS и мы в браузере (не standalone)
    if (ios && !webview && !(window.navigator.standalone === true)) {
      setIsInstallable(true);
      logSuspectedUninstall({
        isInstallable: true,
        platform,
        detectionMode: "ios_browser_installable",
      });
    }

    // Handle Install Prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);

      logSuspectedUninstall({
        isInstallable: true,
        platform,
        detectionMode: "beforeinstallprompt",
      });
    };

    const handleAppInstalled = () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
      setIsInstallPromptPending(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      isMounted = false;
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  // Auto-show install guide on every fresh open for users who haven't installed.
  // Shows once per browser session (sessionStorage) so it appears each time the app
  // is opened, but not on every route change within the same session.
  useEffect(() => {
    const isStandaloneMode =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandaloneMode) return;

    let shown = false;
    try {
      shown = sessionStorage.getItem("mgfood_install_guide_shown") === "1";
    } catch (_e) {
      // ignore storage errors
    }

    if (shown) return;

    const t = setTimeout(() => {
      setShowInstallGuide(true);
      try {
        sessionStorage.setItem("mgfood_install_guide_shown", "1");
      } catch (_e) {
      // ignore storage errors
    }
    }, 1200);

    return () => clearTimeout(t);
  }, []);

  const installApp = async () => {
    if (deferredPrompt) {
      setIsInstallPromptPending(true);

      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") {
          setDeferredPrompt(null);
          setIsInstallable(false);
        }
      } finally {
        setIsInstallPromptPending(false);
      }
    } else if (isIOS) {
      setShowInstallGuide(true);
    }
  };

  const openInSystemBrowser = () => {
    const currentUrl = window.location.href;
    if (isAndroid) {
      // Intent for Chrome on Android
      window.location.href = `intent://${currentUrl.replace(/^https?:\/\//, "")}#Intent;scheme=https;package=com.android.chrome;end`;
    } else if (isIOS) {
      // Direct users to open in Safari
      alert("Для установки приложения, пожалуйста, откройте этот сайт в Safari.");
    }
  };

  const value = {
    isInstallable,
    isInstallPromptPending,
    isStandalone,
    isIOS,
    isAndroid,
    isWebview,
    showInstallGuide,
    setShowInstallGuide,
    installApp,
    openInSystemBrowser,
  };

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
};
